import { ajvResolver } from '@hookform/resolvers/ajv'
import { NavigateBefore, NavigateNext } from '@mui/icons-material'
import { AxiosError } from 'axios'
import { Form } from 'components/form'
import Header from 'components/header'
import { HeaderModule } from 'components/header-module'
import Page from 'components/page'
import Wrapper from 'components/wrapper'
import { useAlert } from 'contexts/alert'
import { useAuth } from 'contexts/auth'
import { useCallback, useEffect, useState } from 'react'
import { SubmitHandler, useForm } from 'react-hook-form'
import { useHistory } from 'react-router-dom'
import ThemeEditSchema, { IThemeEditFormData } from 'schema/common/theme-edit'
import api from 'services/api'
import Application from '../form/application'
import Validation from '../form/validation'
import LogoAndColors from './logo-and-colors'

const steps: string[] = ['Editar', 'Aplicações', 'Válide sua configuração']

const ThemeEditForm = () => {
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [activeStep, setActiveStep] = useState<number>(0)
  const { user, updateUserData } = useAuth()
  const { openAlert } = useAlert()
  const history = useHistory()

  const formData = useForm({
    resolver: ajvResolver(ThemeEditSchema),
    defaultValues: {
      baseLogoFile: '',
      whiteLogoFile: '',
      blackLogoFile: '',
      baseLogo: '',
      whiteLogo: '',
      blackLogo: '',
      primaryColor: '000000',
      secondaryColor: '000000',
      terciaryColor: '000000',
    },
  })

  const { handleSubmit, watch, setValue, getValues } = formData

  const onSubmit: SubmitHandler<IThemeEditFormData> = useCallback(
    async (data) => {
      try {
        setIsLoading(true)
        const formData = new FormData()

        if (data.baseLogoFile) {
          formData.append('baseLogo', data.baseLogoFile)
        }

        if (data.whiteLogoFile) {
          formData.append('whiteLogo', data.whiteLogoFile)
        }

        if (data.blackLogoFile) {
          formData.append('blackLogo', data.blackLogoFile)
        }

        formData.append('primaryColor', data.primaryColor.replace('#', ''))
        formData.append('secondaryColor', data.secondaryColor.replace('#', ''))
        formData.append('terciaryColor', data.terciaryColor.replace('#', ''))

        await api.put('/themes', formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        })

        await updateUserData()

        openAlert('Tema Editado com sucesso', 'success')
        setIsLoading(false)
        history.push('/')
      } catch (error) {
        setIsLoading(false)
        const err = error as AxiosError
        console.log(err.response)
        openAlert('Erro ao Editado tema', 'error')
      }
    },
    [history]
  )

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1)
  }

  const handleNext = async () => {
    if (activeStep !== 2) {
      return setActiveStep((prevActiveStep) => prevActiveStep + 1)
    }

    const values = getValues()

    const data: IThemeEditFormData = {
      baseLogoFile: values.baseLogoFile,
      whiteLogoFile: values.whiteLogoFile,
      blackLogoFile: values.blackLogoFile,
      baseLogo: values.baseLogo,
      whiteLogo: values.whiteLogo,
      blackLogo: values.blackLogo,
      primaryColor: values.primaryColor,
      secondaryColor: values.secondaryColor,
      terciaryColor: values.terciaryColor,
    }

    await onSubmit(data)
  }

  const validateStepButton = () => {
    if (activeStep == 0) {
      return watch('baseLogo') == null || watch('whiteLogo') == null
    }
  }

  const handleGetTheme = async () => {
    try {
      if (!user) {
        return null
      }

      const userThemeBaseSplitted = user.theme!.baseLogo.split('/')
      const userThemeBaseSplittedLength = userThemeBaseSplitted.length
      const userThemeBase = userThemeBaseSplitted[userThemeBaseSplittedLength - 1]
      const userThemeBaseLogo = userThemeBase == '' || userThemeBase == 'null' ? null : user.theme!.baseLogo

      const userThemeWhiteSplitted = user.theme!.whiteLogo.split('/')
      const userThemeWhiteSplittedLength = userThemeWhiteSplitted.length
      const userThemeWhite = userThemeWhiteSplitted[userThemeWhiteSplittedLength - 1]
      const userThemeWhiteLogo = userThemeWhite == '' || userThemeWhite == 'null' ? null : user.theme!.whiteLogo

      const userThemeBlackSplitted = user.theme!.blackLogo.split('/')
      const userThemeBlackSplittedLength = userThemeBlackSplitted.length
      const userThemeBlack = userThemeBlackSplitted[userThemeBlackSplittedLength - 1]
      const userThemeBlackLogo = userThemeBlack == '' || userThemeBlack == 'null' ? null : user.theme!.blackLogo

      const primaryColor = user.theme!.primary == null ? '000000' : user.theme!.primary.replace('#', '')
      const secondaryColor = user.theme!.secondary == null ? '000000' : user.theme!.secondary.replace('#', '')
      const terciaryColor = user.theme!.terciary == null ? '000000' : user.theme!.terciary.replace('#', '')

      setValue('baseLogo', userThemeBaseLogo as string)
      setValue('whiteLogo', userThemeWhiteLogo as string)
      setValue('blackLogo', userThemeBlackLogo as string)
      setValue('primaryColor', primaryColor)
      setValue('secondaryColor', secondaryColor)
      setValue('terciaryColor', terciaryColor)
    } catch (error) {
      console.log(error)
    }
  }

  useEffect(() => {
    if (user && !user.isSeller) {
      history.push('/')
    }
  }, [user])

  useEffect(() => {
    handleGetTheme()
  }, [])

  return (
    <Wrapper>
      <Header>
        <HeaderModule.title text='Personalize sua Conta' />
      </Header>

      <Page>
        <Form.Root onHandleSubmit={handleSubmit(onSubmit)}>
          <Form.Stepper
            activeStep={activeStep}
            steps={steps}
          />

          <Form.Body>
            {activeStep === 0 && <LogoAndColors formData={formData} />}
            {activeStep === 1 && <Application formData={formData} />}
            {activeStep === 2 && <Validation formData={formData} />}
          </Form.Body>

          <Form.Divisor />

          <Form.Footer>
            <Form.Button
              label={activeStep === 2 ? 'Finalizar' : 'Continuar'}
              type={'button'}
              variant='contained'
              isLoading={isLoading}
              isDisabled={validateStepButton()}
              endIcon={<NavigateNext />}
              onClick={handleNext}
            />

            {activeStep !== 0 && (
              <Form.Button
                label='Voltar'
                type='back'
                variant='text'
                onClick={handleBack}
                startIcon={<NavigateBefore />}
              />
            )}
          </Form.Footer>
        </Form.Root>
      </Page>
    </Wrapper>
  )
}

export default ThemeEditForm
